import {
  AdDensityMetricEvent,
  AuctionPartnerEvent,
  CoreWebVitalsThresholdEvent,
  GenericEvent,
  isAdDensityMetricEvent,
  isAuctionPartnerEvent,
  isCoreWebVitalsThresholdEvent,
  isPageChangedEvent,
  isPageLoadEvent,
  isProductLikedEvent,
  isProductUnlikedEvent,
  isSourcepointConsentCapturedEvent,
  PageChangedEvent,
  PageLoadEvent,
  ProductLikedEvent,
  ProductUnlikedEvent,
  SourcepointConsentCapturedEvent,
} from "types/Events";
import { getWindowHostname } from "utils/browserLocation";
import log from "utils/log";
import { mapCoreWebVitalsToProto } from "utils/mapCoreWebVitalsToProto";

import { resolveGargantuanRequestUrl } from "./url";

const eventMapping: { [key: string]: string } = {
  pageLoadEvent: "/.events-handler/future.adtech.freyr.v1.PageViewEvent",
  pageChangedEvent: "/.events-handler/future.adtech.freyr.v1.PageChangedEvent",
  pageChangeEvent: "/.events-handler/future.adtech.freyr.v1.PageChangedEvent",
  auctionPartnerEvent:
    "/.events-handler/future.adtech.bordeaux.v1.AuctionPartnerBidRequestCompletedEvent",
  productLikedEvent:
    "/.events-handler/future.adtech.freyr.v1alpha.ProductLikedEvent",
  productUnlikedEvent:
    "/.events-handler/future.adtech.freyr.v1alpha.ProductUnlikedEvent",
  coreWebVitalsThresholdEvent:
    "/.events-handler/future.web.vanilla.fte.v1.CoreWebVitalsThresholdEvent",
  adDensityMetricEvent:
    "/.events-handler/future.adtech.freyr.v1.AdDensityMetricEvent",
  sourcepointConsentCapturedEvent:
    "/.events-handler/future.web.thirdparty.sourcepoint.v1.SourcepointConsentCapturedEvent",
};

type EventEndpointOverride = {
  eventName: string;
  appliesTo: (url: string) => boolean;
  endpoint: string;
};

const includesAny = (haystack: string, needles: readonly string[]): boolean =>
  needles.some((needle) => haystack.includes(needle));

const eventEndpointOverrides: readonly EventEndpointOverride[] = [
  {
    eventName: "pageLoadEvent",
    appliesTo: (url) =>
      includesAny(url, ["magazinesdirect", "arcade", "sciencealert"]),
    endpoint:
      "https://eventsproxy.gargantuan.futureplc.com/future.adtech.freyr.v1.PageViewEvent",
  },
  {
    eventName: "auctionPartnerEvent",
    appliesTo: (url) =>
      includesAny(url, ["magazinesdirect", "arcade", "sciencealert"]),
    endpoint:
      "https://eventsproxy.gargantuan.futureplc.com/future.adtech.bordeaux.v1.AuctionPartnerBidRequestCompletedEvent",
  },
];

const resolveEventEndpoint = (
  eventName: string,
  url: string = window.location.href
): string =>
  eventEndpointOverrides.find(
    (override) => override.eventName === eventName && override.appliesTo(url)
  )?.endpoint ?? eventMapping[eventName];

const isPageEvent = (event: GenericEvent): boolean =>
  event.name === "pageLoadEvent" || event.name === "pageChangedEvent";

const toSafePayload = (payload: Record<string, unknown>) => {
  const safePayload: Record<string, unknown> = {};
  const descriptors = Object.getOwnPropertyDescriptors(payload);

  Object.entries(descriptors).forEach(([key, descriptor]) => {
    if ("value" in descriptor) {
      safePayload[key] = descriptor.value;
    }
  });

  return safePayload;
};

const mapPagePayload = (payload: Record<string, unknown>) => {
  try {
    const userValue = payload.user;
    const user =
      userValue && typeof userValue === "object" && !Array.isArray(userValue)
        ? (userValue as Record<string, unknown>)
        : {};
    const ua =
      typeof user.ua === "string" && user.ua.length > 0
        ? user.ua
        : navigator.userAgent;

    return {
      ...payload,
      user: {
        ...user,
        ua,
      },
    };
  } catch {
    return toSafePayload(payload);
  }
};

export const sendToGargantuan = (
  data:
    | PageLoadEvent
    | PageChangedEvent
    | AuctionPartnerEvent
    | ProductLikedEvent
    | ProductUnlikedEvent
    | CoreWebVitalsThresholdEvent
    | AdDensityMetricEvent
    | SourcepointConsentCapturedEvent
): Promise<Response> => {
  let payload = data.payload;

  if (isPageEvent(data)) {
    payload = mapPagePayload(data.payload as Record<string, unknown>) as any;
  } else if (isProductLikedEvent(data) || isProductUnlikedEvent(data)) {
    payload = {
      ...data.payload,
    } as any;
  } else if (isCoreWebVitalsThresholdEvent(data)) {
    payload = mapCoreWebVitalsToProto(data.payload);
  }

  const endpoint = resolveEventEndpoint(data.name);
  const requestUrl = resolveGargantuanRequestUrl(endpoint, getWindowHostname());

  return fetch(requestUrl, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
};

const allowedEvent = (
  event: GenericEvent
): event is
  | PageLoadEvent
  | PageChangedEvent
  | AuctionPartnerEvent
  | ProductLikedEvent
  | ProductUnlikedEvent
  | CoreWebVitalsThresholdEvent
  | AdDensityMetricEvent
  | SourcepointConsentCapturedEvent =>
  isPageLoadEvent(event) ||
  isPageChangedEvent(event) ||
  isPageEvent(event) ||
  isAuctionPartnerEvent(event) ||
  isProductLikedEvent(event) ||
  isProductUnlikedEvent(event) ||
  isCoreWebVitalsThresholdEvent(event) ||
  isAdDensityMetricEvent(event) ||
  isSourcepointConsentCapturedEvent(event);

export default (data: GenericEvent): void => {
  if (allowedEvent(data)) {
    sendToGargantuan(data).catch((err) => {
      log.warn(err.message);
    });
  }
};
